import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight,
  MessageSquare, Type, Languages, Search, FileText, ListChecks, Pencil, HelpCircle,
  Sparkles, RefreshCw, Users,
} from 'lucide-react';
import { GlassPage } from '../components/chrome';
import { GlassesModel } from '../components/model';
import { ProductCards } from '../components/products';

const FEATURES = [
  { icon: MessageSquare, title: 'Caption', desc: 'Loud room, four people talking. Every word gets subtitled.' },
  { icon: Type, title: 'Recall', desc: 'Mid-sentence, you forget the word. It appears on the lens instantly.' },
  { icon: Languages, title: 'Translate', desc: 'The cashier speaks Italian, but you see English automatically.' },
  { icon: Search, title: 'Ask', desc: 'You or someone else asks a question. The answer is there instantly.' },
  { icon: FileText, title: 'Teleprompt', desc: 'Your script scrolls as you speak. You keep eye contact the whole presentation.' },
  { icon: ListChecks, title: 'Recap', desc: 'Forgot something after a conversation? The summary is waiting for you.' },
  { icon: Pencil, title: 'Note', desc: 'Say the idea. It is written down before you forget it.' },
  { icon: HelpCircle, title: 'Question', desc: 'Asked too many things at once? It is all displayed for you to answer.' },
];

const MARKETPLACE = [
  { icon: Sparkles, title: 'Add abilities', desc: 'Find what fits your day and add it in a tap. It lands on your lens with nothing to set up.' },
  { icon: RefreshCw, title: 'Always growing', desc: 'Updates come over the air, so the pair you buy this year keeps learning new tricks the next.' },
  { icon: Users, title: 'Built by many', desc: 'Anyone can build for the lens, so what KorsaOS can do never stops growing.' },
];

// --- Components ---

const FadeInSection = ({ children }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(domRef.current);
        }
      });
    }, { threshold: 0.1 });

    if (domRef.current) {
      observer.observe(domRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={domRef}
      className={`transition-all duration-1000 ease-out transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      {children}
    </div>
  );
};

const ScrollRevealText = ({ content, className }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!elementRef.current) return;
      const { top, height } = elementRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const startReveal = windowHeight * .8;
      const endReveal = windowHeight * .6;

      const distance = startReveal - top;
      const totalRange = startReveal - endReveal + height * 0.5;

      const progress = Math.max(0, Math.min(1, distance / totalRange));
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const paragraphs = content.split('\n').filter(p => p.trim() !== '');
  const allText = paragraphs.join(' ');
  const words = allText.split(' ').filter(w => w.length > 0);
  const totalWordCount = words.length;
  const activeWordIndex = Math.ceil(scrollProgress * totalWordCount) - 1;

  let globalWordIndex = 0;

  return (
    <div ref={elementRef} className={className}>
      {paragraphs.map((paragraph, pIdx) => {
        const paragraphWords = paragraph.split(' ');
        return (
          <p key={pIdx} className="mb-8 last:mb-0">
            {paragraphWords.map((word, wIdx) => {
              const isWordActive = globalWordIndex <= activeWordIndex;
              globalWordIndex++;
              return (
                <span key={wIdx}>
                  {word.split('').map((letter, lIdx) => (
                    <span
                      key={lIdx}
                      className={`transition-colors duration-200 ${isWordActive ? 'text-zinc-900' : 'text-zinc-300'}`}
                    >
                      {letter}
                    </span>
                  ))}
                  {' '}
                </span>
              );
            })}
          </p>
        );
      })}
    </div>
  );
}

// External coverage about Korsa. Swap in real article links as they land.
// Each entry: source (publication), date, title (headline), blurb, url.
const NEWS_ARTICLES = [
  {
    source: 'YouTube',
    date: 'Nov 2025',
    title: 'Korsa demos its 2025 prototype',
    blurb: 'The first working prototype on camera, with live word retrieval and teleprompting running on real hardware.',
    url: 'https://www.youtube.com/@Korsahq'
  },
  {
    source: 'X / Twitter',
    date: '2026',
    title: 'Follow the build in public',
    blurb: 'Product updates, milestones, and announcements straight from the team.',
    url: 'https://x.com/korsahq'
  },
  {
    source: 'TikTok',
    date: '2026',
    title: 'Korsa behind the scenes',
    blurb: 'Short-form looks at the glasses, the lab, and the people building them.',
    url: 'https://www.tiktok.com/@korsahq'
  }
];

const App = () => {
  // If we arrive with a hash (e.g. the order page's Product tab links to
  // /#korsaos), scroll to it once the layout settles, after the global
  // ScrollToTop has reset to the top.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
      else el.scrollIntoView({ behavior: 'smooth' });
    }, 250);
    return () => clearTimeout(t);
  }, []);

  return (
    <GlassPage>
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        <div
          className="absolute inset-0 z-1 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0) 30%, rgba(238, 241, 246, 0.85) 100%)',
          }}
        />

        <GlassesModel modelPath="/models/model.glb" className="absolute top-[35%] left-[5%] w-[90%] h-[60%] md:top-[16%] md:left-0 md:w-full md:h-full z-0 pointer-events-none" />

        {/* Headline at the top, clear of the glasses */}
        <div className="relative z-10 flex flex-col items-center text-center px-6 md:px-12 pt-28 md:pt-32">
          <h1 className="text-[9vw] md:text-[4.5vw] font-bold leading-[0.95] tracking-tighter mb-4 uppercase text-zinc-900">
            Think Clearly.<br/>
            <span className="text-zinc-500">Speak Confidently</span>
          </h1>

          {/* CTA above the glasses */}
          <div className="flex justify-center mt-4 md:mt-5">
            <Link to="/order" className="btn-dark w-44">
              Pre-order
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="relative py-16 md:py-24 px-4 md:px-6">
        <FadeInSection>
          <div className="glass max-w-[1600px] mx-auto px-8 md:px-16 py-16 md:py-24">
            <div className="grid md:grid-cols-12 gap-12">
              <div className="md:col-span-12">
                <h2 className="text-5xl md:text-[7vw] font-bold tracking-tighter leading-[0.9] uppercase mb-16 text-zinc-900 text-center">
                  Our Mission.
                </h2>
                <div className="max-w-3xl mx-auto space-y-8 text-xl md:text-2xl font-medium leading-snug text-center">
                  <ScrollRevealText
                    className=""
                    content={`Korsa is building the next generation of smart glasses using cognitive assistive technology to enhance how people communicate in everyday life.\nWe created smart glasses that support you as you talk. They look like ordinary glasses but to you, they are an essential piece of your day that makes communication easier.`}
                  />
                </div>
              </div>
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* Glasses + App */}
      <FadeInSection>
        <ProductCards id="products" />
      </FadeInSection>

      {/* KorsaOS + Marketplace */}
      <section id="korsaos" className="py-16 md:py-24 px-4 md:px-6 scroll-mt-24">
        <FadeInSection>
          <div className="glass max-w-[1600px] mx-auto px-8 md:px-16 py-16 md:py-20">
            <div className="mb-10 md:mb-14 text-center">
              <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-zinc-900">Korsa<span className="text-zinc-500">OS</span></h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="rounded-2xl bg-white/80 border border-white shadow-[0_10px_30px_rgba(31,38,135,0.12)] p-5 hover:-translate-y-1 transition-transform duration-300 flex flex-col items-center text-center">
                    <Icon size={20} strokeWidth={2} className="text-zinc-600 mb-3" />
                    <h3 className="text-lg font-bold tracking-tight text-zinc-900 mb-1.5">{f.title}</h3>
                    <p className="text-sm text-zinc-600 leading-relaxed">{f.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Marketplace */}
            <div className="mt-12 md:mt-16 pt-10 md:pt-12 border-t border-zinc-900/5">
              <div className="text-center mb-8 md:mb-10">
                <h3 className="text-3xl md:text-5xl font-bold tracking-tighter uppercase text-zinc-900 mb-4">The marketplace</h3>
                <p className="text-base text-zinc-600 leading-relaxed max-w-xl mx-auto">
                  One pair of glasses that keeps getting better. New abilities arrive the way
                  apps do on a phone.
                </p>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 md:gap-5">
                {MARKETPLACE.map((m) => {
                  const Icon = m.icon;
                  return (
                    <div key={m.title} className="rounded-2xl bg-white/80 border border-white shadow-[0_10px_30px_rgba(31,38,135,0.12)] p-5 md:p-6 hover:-translate-y-1 transition-transform duration-300 flex flex-col items-center text-center">
                      <Icon size={20} strokeWidth={2} className="text-zinc-600 mb-3" />
                      <h3 className="text-lg font-bold tracking-tight text-zinc-900 mb-1.5">{m.title}</h3>
                      <p className="text-sm text-zinc-600 leading-relaxed">{m.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* Newsroom */}
      <section id="news" className="py-16 md:py-24 px-4 md:px-6">
        <FadeInSection>
          <div className="glass max-w-[1600px] mx-auto px-8 md:px-16 py-16 md:py-20">
            <div className="flex flex-col items-center text-center gap-4 mb-12 md:mb-16">
              <h2 className="text-4xl md:text-6xl font-bold tracking-tighter uppercase text-zinc-900">Newsroom</h2>
              <p className="text-sm text-zinc-600 max-w-xs">Korsa in the news: press, coverage, and mentions.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {NEWS_ARTICLES.map((article) => (
                <a
                  key={article.title}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col rounded-2xl bg-white/40 border border-white/70 p-6 md:p-8 hover:bg-white/70 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(31,38,135,0.12)] transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-900">{article.source}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{article.date}</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-900 mb-3">{article.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed flex-1">{article.blurb}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-900">
                    Read
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-12 flex justify-center">
              <Link
                to="/contact"
                state={{ subject: 'Press Inquiry' }}
                className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Press inquiries → Contact us
              </Link>
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* Careers Preview */}
      <section className="py-16 md:py-24 px-4 md:px-6">
        <FadeInSection>
          <div className="glass max-w-[1600px] mx-auto px-8 md:px-16 py-16 md:py-20">
            <div className="mb-12 md:mb-16 text-center">
              <h2 className="text-5xl md:text-7xl font-bold tracking-tighter uppercase leading-[0.9] text-zinc-900">JOIN OUR TEAM</h2>
            </div>
            <div className="border-t border-zinc-900/5">
              {[
                { title: "Machine Learning Engineer", dept: "Software & AI", desc: "Model development and optimization" },
                { title: "Data Engineer", dept: "Software & AI", desc: "Data pipeline and processing systems" },
                { title: "Optical Engineer", dept: "Hardware", desc: "Lens systems and projection technology" },
                { title: "Electrical Engineer", dept: "Hardware", desc: "Circuit design and power management" },
                { title: "Audio Engineer", dept: "Hardware", desc: "Sound processing and optimization" },
              ].map((job, i) => (
                <Link key={i} to="/careers" className="group border-b border-zinc-900/5 py-5 md:py-6 flex items-center justify-between hover:bg-white/40 rounded-xl transition-all duration-300 px-2 md:px-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-8">
                    <h3 className="text-lg md:text-xl font-bold tracking-tight text-zinc-900">{job.title}</h3>
                    <span className="text-xs text-zinc-600">{job.desc}</span>
                  </div>
                  <ArrowRight size={16} className="text-zinc-500 group-hover:text-zinc-900 group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              ))}
            </div>
            <div className="mt-12 flex justify-center">
              <Link to="/careers" className="btn-dark">
                View All Positions <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </FadeInSection>
      </section>
    </GlassPage>
  );
};

export default App;
